import React, { AnimationEventHandler, Fragment, useMemo, FC } from "react";
import { FlatListItem } from "../../../components/partials/FlatList";
import { StartListEntry } from "../../../models/startlist.model";
import { AnimatedFlatList } from "./components/AnimatedFlatList";
import { Flag } from "./components/Flag";
import { usePagination } from "./hooks/usePagination";
import { PaginatedArea } from "./components/AnimatedArea";
import { SquareList } from "./components/SquareList";
import { SponsorLogo } from "./components/SponsorLogo";
import {
  SecondaryNameColumn,
  PrimaryNameColumn,
  StartGroupColumn,
  TeamColumn,
} from "./components/Column";
import { Test } from "../../../models/test.model";

type Phase = "PREL" | "AFIN" | "BFIN" | "CFIN" | "FIN";

const LIGHT_BACKGROUNDS = ["white", "yellow"];

const StartListItem: React.FC<FlatListItem<StartListEntry, Phase>> = ({
  item,
  show,
  onHidden,
  onShown,
  extraData: phase,
}) => {
  const animationEnd: AnimationEventHandler<HTMLLIElement> = (event) => {
    if (event.animationName === "scaleOut") {
      onHidden?.();
    }

    if (event.animationName === "scaleIn") {
      onShown?.();
    }
  };

  const textColor = LIGHT_BACKGROUNDS.includes(item.color) ? "black" : "white";

  return (
    <li
      className={`flatlist-item ${show ? "show" : "hide"}`}
      style={{ gridTemplateColumns: "1fr 1rem" }}
      onAnimationEnd={animationEnd}
    >
      <div
        className="row-content"
        style={{
          gridTemplateColumns: "1fr 1fr 2ch 4ch",
        }}
      >
        <div
          className="row-marker"
          style={{
            backgroundColor:
              phase !== "PREL" ? `var(--${item.color})` : "var(--blue)",
            color: textColor,
          }}
        >
          {item.startGroup}
        </div>
        <div>{item.rider?.fullname}</div>
        <div>{item.horse?.horseName}</div>
        <div className="text-right">
          {item.rider?.ageGroup?.split(" ").map((word) => word[0])}
        </div>
        <Flag
          countryCode={item.rider?.team ?? ""}
          style={{
            height: "1em",
            alignSelf: "end",
            marginLeft: "auto",
          }}
        />
      </div>
    </li>
  );
};

type StartListProps = {
  startList: StartListEntry[];
  test: Test;
  phase: string;
};

const StartListClassic: React.FC<StartListProps> = ({
  startList,
  test,
  phase,
}) => {
  return (
    <AnimatedFlatList
      header={
        <>
          <span>
            {test.testName} {phase}
          </span>
          -<small>Start List</small>
        </>
      }
      headerImg={test.sponsorLogo ?? "assets/img/ICeCompass_Logo_Final6.png"}
      items={startList}
      RenderComponent={StartListItem}
      itemsPerPage={10}
      extraData={phase}
      timePerPage={15000}
    />
  );
};

const StartListSquare: React.FC<StartListProps> = ({
  startList,
  test,
  phase,
}) => {
  const { page, nextPage, itemsPerPage } = usePagination(startList);

  const hasFlag = page.some(
    (item) => item?.rider?.team && item.rider?.team !== ""
  );

  return (
    <SquareList style={{ width: "80vw" }}>
      <header>
        <h1>
          {test.testName} {phase} - <small>Starting Order</small>
        </h1>
      </header>
      <PaginatedArea
        resetKey={test.testName}
        enableAnimation={startList.length > itemsPerPage}
        onHidden={nextPage}
      >
        <div
          className="bs-table"
          style={{
            gridTemplateColumns: `minmax(6ch, max-content)${
              hasFlag ? " max-content " : ""
            } 1fr 1fr`,
          }}
        >
          {page.map((item, index) => (
            <SquareStartListTableRow
              key={item.id}
              item={item}
              phase={phase}
              test={test}
              isLast={index === page.length - 1}
              hasFlag={hasFlag}
            />
          ))}
        </div>
        <SponsorLogo src={test.sponsorLogo} />
      </PaginatedArea>
    </SquareList>
  );
};

const SquareStartListTableRow: FC<{
  item: StartListEntry;
  test: Test;
  phase?: string;
  isLast: boolean;
  hasFlag: boolean;
}> = ({ item, isLast, test, phase, hasFlag }) => {
  return (
    <Fragment key={item.id}>
      <StartGroupColumn startListEntry={item} phase={phase} isLast={isLast} />
      {hasFlag && <TeamColumn team={item.rider?.team} isLast={isLast} />}
      <PrimaryNameColumn entry={item} test={test} isLast={isLast} />
      <SecondaryNameColumn test={test} entry={item} isLast={isLast} />
    </Fragment>
  );
};

export const StartList: React.FC<
  StartListProps & { variant?: "classic" | "square" }
> = ({ variant, phase, ...rest }) => {
  const phaseText = useMemo(() => {
    switch (phase) {
      case "PREL":
        return "Preliminary";
      case "AFIN":
        return "A-final";
      case "BFIN":
        return "B-final";
      case "CFIN":
        return "C-final";
      default:
        return "";
    }
  }, [phase]);

  switch (variant) {
    case "classic":
      return <StartListClassic phase={phaseText} {...rest} />;
    case "square":
    default:
      return <StartListSquare phase={phaseText} {...rest} />;
  }
};
